import { useEffect, useRef } from 'react';

type UseTouchActionProps = {
  containerRef: React.RefObject<HTMLDivElement>;
  onTouchAction?: (overscrollDistance: number, touchEnd: boolean, velocity: number) => void;
};

const useTouchAction = ({ containerRef, onTouchAction }: UseTouchActionProps) => {
  const startYRef = useRef(0);
  const startScrollTopRef = useRef(0);
  const overscrollRef = useRef(0);
  const velocityRef = useRef(0);
  const lastSampleRef = useRef({ y: 0, t: 0 });
  const callbackRef = useRef(onTouchAction);
  callbackRef.current = onTouchAction;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e: TouchEvent) => {
      const { clientY } = e.touches[0];
      startYRef.current = clientY;
      startScrollTopRef.current = container.scrollTop;
      overscrollRef.current = 0;
      velocityRef.current = 0;
      lastSampleRef.current = { y: clientY, t: e.timeStamp };
    };

    const handleTouchMove = (e: TouchEvent) => {
      const { clientY } = e.touches[0];
      const last = lastSampleRef.current;
      const dt = e.timeStamp - last.t;
      if (dt > 0) velocityRef.current = ((clientY - last.y) / dt) * 1000;
      lastSampleRef.current = { y: clientY, t: e.timeStamp };

      const newScrollTop = startScrollTopRef.current - (clientY - startYRef.current);
      overscrollRef.current = newScrollTop < 0 ? -newScrollTop : 0;
      callbackRef.current?.(overscrollRef.current, false, velocityRef.current);
    };

    const handleTouchEnd = () => {
      callbackRef.current?.(overscrollRef.current, true, velocityRef.current);
    };

    container.addEventListener('touchstart', handleTouchStart);
    container.addEventListener('touchmove', handleTouchMove);
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
    };
  }, [containerRef]);
};

export default useTouchAction;
